import Phaser from 'phaser';
import {
  PlayerPosition,
  Card as CardType,
  PlayedCard,
  ClientGameState,
  CARD_WIDTH,
  CARD_HEIGHT,
  CARD_RADIUS,
  AnimationEvent,
  AnimationSequenceId,
} from '@hokm/shared';
import { PERSIAN_THEME } from '../../../theme/persianTheme';
import { RENDER_SCALE } from '../../../config';
import { TRICK_CENTER, PlayerPositionConfig } from '../../../config/positions';

export interface TrickAnimatorCallbacks {
  getPositionConfig: () => Record<PlayerPosition, PlayerPositionConfig>;
  getTrickOffsetsConfig: () => Record<PlayerPosition, { x: number; y: number }>;
  toDisplayPosition: (serverPosition: PlayerPosition) => PlayerPosition;
  getGameState: () => ClientGameState | null;
  sendAnimationAck: (event: AnimationEvent, sequenceId: AnimationSequenceId) => void;
  onTrickCollected: () => void;
}

/**
 * Handles trick card animations: playing cards to center, collecting tricks.
 */
export class TrickAnimator {
  private scene: Phaser.Scene;
  private callbacks: TrickAnimatorCallbacks;

  // Trick cards by display position
  private trickCards: Map<PlayerPosition, Phaser.GameObjects.Container> = new Map();
  private processedCardPlays: Set<string> = new Set();
  private isCollecting: boolean = false;
  private lastCollectedTrickNumber: number = 0;

  constructor(scene: Phaser.Scene, callbacks: TrickAnimatorCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  get isCollectingTrick(): boolean {
    return this.isCollecting;
  }

  get lastCollectedTrick(): number {
    return this.lastCollectedTrickNumber;
  }

  getTrickCards(): Map<PlayerPosition, Phaser.GameObjects.Container> {
    return this.trickCards;
  }

  hasProcessedCardPlay(key: string): boolean {
    return this.processedCardPlays.has(key);
  }

  markCardPlayProcessed(key: string): void {
    this.processedCardPlays.add(key);
  }

  /**
   * Create a trick card container with optional trump glow.
   */
  createTrickCardContainer(x: number, y: number, card: CardType, depth: number): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    container.setDepth(depth);

    const textureKey = `card-${card.suit}-${card.rank}`;
    const img = this.scene.add.image(0, 0, textureKey);
    img.setScale(1 / RENDER_SCALE);

    const state = this.callbacks.getGameState();
    if (state?.trumpSuit && card.suit === state.trumpSuit) {
      const glow = this.scene.add.graphics();
      glow.lineStyle(3, PERSIAN_THEME.colors.gold, 0.9);
      glow.strokeRoundedRect(
        -CARD_WIDTH / 2 - 1,
        -CARD_HEIGHT / 2 - 1,
        CARD_WIDTH + 2,
        CARD_HEIGHT + 2,
        CARD_RADIUS + 1
      );
      container.add(glow);
    }

    container.add(img);
    return container;
  }

  /**
   * Animate a card being played to the trick area.
   */
  animateCardToTrick(position: PlayerPosition, card: CardType): void {
    const offset = this.callbacks.getTrickOffsetsConfig()[position];
    const targetX = TRICK_CENTER.x + offset.x;
    const targetY = TRICK_CENTER.y + offset.y;

    // Clear existing card at position
    const existingCard = this.trickCards.get(position);
    if (existingCard) {
      existingCard.destroy();
      this.trickCards.delete(position);
    }

    const start = this.callbacks.getPositionConfig()[position].animationStart;
    const container = this.createTrickCardContainer(start.x, start.y, card, 10);
    container.setScale(0.8);

    this.trickCards.set(position, container);

    this.scene.tweens.add({
      targets: container,
      x: targetX,
      y: targetY,
      scale: 1,
      duration: 300,
      ease: 'Cubic.easeOut',
    });
  }

  /**
   * Play card optimistically from local player's hand.
   */
  playCardOptimistically(startX: number, startY: number, card: CardType): void {
    const offset = this.callbacks.getTrickOffsetsConfig()[PlayerPosition.SOUTH];
    const targetX = TRICK_CENTER.x + offset.x;
    const targetY = TRICK_CENTER.y + offset.y;

    // Clear existing card at SOUTH
    const existingCard = this.trickCards.get(PlayerPosition.SOUTH);
    if (existingCard) {
      existingCard.destroy();
      this.trickCards.delete(PlayerPosition.SOUTH);
    }

    const container = this.createTrickCardContainer(startX, startY, card, 10);
    container.setScale(0.8);
    this.trickCards.set(PlayerPosition.SOUTH, container);

    this.scene.tweens.add({
      targets: container,
      x: targetX,
      y: targetY,
      angle: { from: -5, to: 0 },
      scale: 1,
      duration: 350,
      ease: 'Cubic.easeOut',
    });
  }

  /**
   * Render existing trick cards (for rejoining mid-trick).
   */
  renderTrick(cards: PlayedCard[], currentTrickNumber: number): void {
    if (this.isCollecting) return;

    // Skip stale data
    if (currentTrickNumber > 0 && currentTrickNumber === this.lastCollectedTrickNumber && cards.length > 0) {
      return;
    }

    const state = this.callbacks.getGameState();
    cards.forEach((playedCard) => {
      const player = state?.players.find((p) => p.id === playedCard.playerId);
      if (!player) return;

      const displayPosition = this.callbacks.toDisplayPosition(player.position);
      if (this.trickCards.has(displayPosition)) return;

      const offset = this.callbacks.getTrickOffsetsConfig()[displayPosition];
      const x = TRICK_CENTER.x + offset.x;
      const y = TRICK_CENTER.y + offset.y;

      const container = this.createTrickCardContainer(x, y, playedCard.card, 10 + playedCard.playOrder);
      this.trickCards.set(displayPosition, container);
    });
  }

  /**
   * Animate trick collection to winner position.
   */
  animateCollection(winnerPosition: PlayerPosition, sequenceId?: AnimationSequenceId): void {
    this.isCollecting = true;

    // Record which trick we're collecting at START - prevents stale gameState from re-rendering
    const currentTrickNum = this.callbacks.getGameState()?.trickNumber ?? 0;
    this.lastCollectedTrickNumber = currentTrickNum;

    const collectionTarget = this.callbacks.getPositionConfig()[winnerPosition].offScreen;

    let pendingTweens = this.trickCards.size;
    let cleanupCalled = false;

    const runCleanup = () => {
      if (cleanupCalled) return;
      cleanupCalled = true;

      this.trickCards.clear();
      this.processedCardPlays.clear();
      this.isCollecting = false;

      if (sequenceId) {
        this.callbacks.sendAnimationAck(AnimationEvent.TRICK_COLLECTED, sequenceId);
      }

      this.callbacks.onTrickCollected();
    };

    // Safety fallback
    this.scene.time.delayedCall(2000, runCleanup);

    if (pendingTweens === 0) {
      runCleanup();
      return;
    }

    let delay = 800;
    this.trickCards.forEach((img, position) => {
      const trickOffset = this.callbacks.getTrickOffsetsConfig()[position];
      const trickX = TRICK_CENTER.x + trickOffset.x;
      const trickY = TRICK_CENTER.y + trickOffset.y;

      this.scene.tweens.add({
        targets: img,
        x: collectionTarget.x,
        y: collectionTarget.y,
        alpha: 0,
        scale: 0.7,
        angle: '+=15',
        duration: 450,
        delay: delay,
        ease: 'Cubic.easeIn',
        onStart: () => {
          img.setPosition(trickX, trickY);
          img.setScale(1);
          img.setAngle(0);
        },
        onComplete: () => {
          img.destroy();
          pendingTweens--;
          if (pendingTweens === 0) {
            runCleanup();
          }
        },
      });
      delay += 50;
    });
  }

  /**
   * Clear all trick cards with animation.
   */
  async clearAll(): Promise<void> {
    const animations: Promise<void>[] = [];

    this.trickCards.forEach((container, position) => {
      if (container.visible) {
        const offScreen = this.callbacks.getPositionConfig()[position].offScreen;
        const promise = new Promise<void>((resolve) => {
          this.scene.tweens.add({
            targets: container,
            x: offScreen.x,
            y: offScreen.y,
            alpha: 0,
            duration: 400,
            ease: 'Power2.easeIn',
            onComplete: () => {
              container.removeAll(true);
              container.setVisible(false);
              resolve();
            },
          });
        });
        animations.push(promise);
      }
    });
    this.trickCards.clear();

    await Promise.all(animations);
  }

  reset(): void {
    this.trickCards.forEach((c) => c.destroy());
    this.trickCards.clear();
    this.processedCardPlays.clear();
    this.isCollecting = false;
    this.lastCollectedTrickNumber = 0;
  }

  destroy(): void {
    this.reset();
  }
}
